/**
 * Barrier Control Helper
 * Controla N barreras: una instancia por botón `.barrier-control-btn`.
 * Cada botón trae data-pedestal (slug) y data-direction (entry|exit); el server
 * resuelve el device_id (fuente de verdad: BD). Nada de device_id en el front.
 */

import SweetAlerts from '@/helpers/sweetAlerts';

class BarrierControl {
    /**
     * Obtiene el CSRF token desde las cookies
     */
    static getCookie(name) {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    }

    /**
     * Inicializa un controlador por cada botón de barrera presente en el DOM.
     */
    static init() {
        document.querySelectorAll('.barrier-control-btn').forEach((btn) => {
            // Evitar doble inicialización si init() se llama más de una vez
            if (btn.dataset.barrierInit === '1') return;
            btn.dataset.barrierInit = '1';
            new BarrierControl(btn);
        });
    }

    constructor(btnElement) {
        this.btnElement = btnElement;
        this.textElement = btnElement.querySelector('.barrier-status-text');
        this.iconElement = btnElement.querySelector('.barrier-status-icon');

        // El server resuelve slug+direction -> device_id (fuente de verdad: BD).
        this.pedestalSlug = btnElement.dataset.pedestal || 'entrada-hora';
        this.direction = btnElement.dataset.direction || 'entry';

        // Etiqueta original (nombre del pedestal) para restaurar tras procesar.
        this.label = this.textElement ? this.textElement.textContent.trim() : 'Control Barrera';
        this.isSending = false;

        this.btnElement.addEventListener('click', () => this.toggle());
    }

    showProcessing(message = 'Procesando...') {
        if (!this.btnElement || !this.textElement || !this.iconElement) return;
        this.btnElement.disabled = true;
        this.btnElement.className = 'btn btn-warning w-100 d-flex align-items-center justify-content-between mb-2 barrier-control-btn';
        this.textElement.textContent = message;
        this.iconElement.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
        this.iconElement.className = 'barrier-status-icon badge badge-phoenix badge-phoenix-warning fs-9';
    }

    showIdle() {
        if (!this.btnElement || !this.textElement || !this.iconElement) return;
        this.btnElement.disabled = false;
        this.btnElement.className = 'btn btn-primary w-100 d-flex align-items-center justify-content-between mb-2 barrier-control-btn';
        this.textElement.textContent = this.label;
        this.iconElement.innerHTML = '<i class="fas fa-road"></i>';
        this.iconElement.className = 'barrier-status-icon badge badge-phoenix badge-phoenix-primary fs-9';
    }

    _query() {
        return `pedestal=${encodeURIComponent(this.pedestalSlug)}&direction=${encodeURIComponent(this.direction)}`;
    }

    async getCurrentStatus() {
        const response = await fetch(`/settings/api/barrier/status/?${this._query()}`);
        const data = await response.json();
        if (!data.success) {
            throw new Error('No se pudo obtener el estado de la barrera');
        }
        return data.status;
    }

    async sendCommand() {
        const csrfToken = BarrierControl.getCookie('csrftoken');
        const response = await fetch('/settings/api/barrier/open/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify({ pedestal: this.pedestalSlug, direction: this.direction }),
        });
        const data = await response.json();
        if (!data.success) {
            throw new Error(data.error || 'Error al enviar comando');
        }
        return data;
    }

    /**
     * Espera y verifica que el estado cambió al esperado
     */
    async waitForStateChange(expectedState, maxAttempts = 15, intervalMs = 1000) {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
            try {
                const currentStatus = await this.getCurrentStatus();
                if (currentStatus === expectedState) return true;
            } catch (error) {
                console.error(`Intento ${attempt}/${maxAttempts} falló:`, error);
            }
        }
        return false;
    }

    /**
     * Controla la barrera de forma inteligente (toggle según estado actual).
     */
    async toggle() {
        if (this.isSending) return;
        this.isSending = true;

        try {
            this.showProcessing('Consultando estado...');
            const currentStatus = await this.getCurrentStatus();

            let targetState, actionText;
            if (currentStatus === 'CLOSED') {
                targetState = 'OPEN';
                actionText = 'Abriendo barrera...';
            } else if (currentStatus === 'OPEN') {
                targetState = 'CLOSED';
                actionText = 'Cerrando barrera...';
            } else {
                targetState = 'OPEN';
                actionText = 'Abriendo barrera...';
            }

            this.showProcessing(actionText);
            await this.sendCommand();

            this.showProcessing('Esperando respuesta...');
            const stateChanged = await this.waitForStateChange(targetState, 15, 1000);

            if (stateChanged) {
                const successMessage = targetState === 'OPEN'
                    ? `${this.label}: barrera abierta`
                    : `${this.label}: barrera cerrada`;
                SweetAlerts.success('Éxito', successMessage);
            } else {
                SweetAlerts.warning(
                    'Timeout',
                    'El comando se envió pero la barrera no respondió en 15 segundos. Verificá el estado manualmente.'
                );
            }
        } catch (error) {
            console.error('Error al controlar barrera:', error);
            SweetAlerts.error('Error', error.message || 'No se pudo controlar la barrera');
        } finally {
            this.showIdle();
            this.isSending = false;
        }
    }
}

export default BarrierControl;
